// The essence dosing products' figures (docs/claude/products.md, *Essence
// dosers*): their sizes, and the solids (`PartSolid3d`, by role) every view of
// them is made from — renderer/builtModels.ts makes them the model the 3D view
// builds and a section cuts. Their plan and ports, drawn from the same figures,
// are essenceDosers.ts's: nothing here reads a product's plan, ports or name,
// so the section bundle takes this module and none of them (test/section.test.mjs).
// - The essence doser: one pump on a black support shelf, its tank beneath. The
//   shelf is Stenal's 3D model mensola_3D.step (drawn in cm): the back panel, the
//   base, the stop at the base's front and the two 45° braces. The pump and the
//   tank are the drawing dosatore_essenze.dxf's, the tank made as deep as the base
//   holds behind the stop.
// - The Aromarium: four pumps under a shelf carrying the Aromarium controller, on
//   a black board on the wall, the four tanks on the floor beneath, each piped to
//   its pump. From the drawing aromarium.dxf.
// Each is drawn with its wall side −y, its heights its front view's, then turned
// half round (never mirrored, shared/turnHalf.ts): a product stands with its
// front to −y, its wall side +y.

import type {
  PartSolid3d,
  Vec2,
} from './types';
import { turnSolid } from './turnHalf';

/** Each role's colour; the board's is the product's own (a model's `body`). */
export const DOSER_COLOURS = {
  board: '#1f1f1f',
  box: '#e8e8e6',
  cap: '#1a1a1a',
  head: '#e6e6e2',
  pipe: '#d4d8da',
  pump: '#262626',
  shelf: '#e9dfc8',
  tank: '#f2f2ee',
  window: '#9fa6ab',
} as const;

export interface Vec3 { x: number; y: number; z: number }

export const rect = (x0: number, y0: number, x1: number, y1: number): Vec2[] => [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
const circle = (c: Vec2, r: number, n = 32): Vec2[] => Array.from({ length: n }, (_, i) => ({ x: c.x + r * Math.cos((2 * Math.PI * i) / n), y: c.y + r * Math.sin((2 * Math.PI * i) / n) }));

const prism = (role: string, ring: Vec2[], z0: number, z1: number): PartSolid3d => ({ kind: 'prism', role, ring, z0, z1 });
/** A convex profile in a vertical plane — (y, z) along x, (x, z) along y —
 *  extruded level from `from` to `to`: the hull of its corners at both ends. */
const levelHull = (role: string, axis: 'x' | 'y', profile: Vec2[], from: number, to: number): PartSolid3d => ({
  kind: 'hull', role,
  pts: profile.flatMap((q): Array<[number, number, number]> => (axis === 'x' ? [[from, q.x, q.y], [to, q.x, q.y]] : [[q.x, from, q.y], [q.x, to, q.y]])),
});
const pipe = (path: Vec3[]): PartSolid3d => ({ kind: 'pipe', role: 'pipe', path: path.map((p): [number, number, number] => [p.x, p.y, p.z]), r: PIPE_R });

/** A tank (a jerrycan) on the floor or a base: its plan a rectangle with its
 *  corners rounded, its cap a disc 71.6 back from its front. */
export interface Tank { x: number; hw: number; y0: number; y1: number; z0: number; z1: number }
const TANK_CORNER = 30;
export const CAP = { back: 71.6, r: 22.9, rise: 33 } as const;

export function tankRing(t: Tank, n = 8): Vec2[] {
  const r = TANK_CORNER, out: Vec2[] = [];
  const x0 = t.x - t.hw, x1 = t.x + t.hw;
  const corners: Array<[number, number, number]> = [[x1 - r, t.y0 + r, -90], [x1 - r, t.y1 - r, 0], [x0 + r, t.y1 - r, 90], [x0 + r, t.y0 + r, 180]];
  for (const [cx, cy, a0] of corners) {
    for (let i = 0; i <= n; i++) {
      const a = ((a0 + (90 * i) / n) * Math.PI) / 180;
      out.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
  }
  return out;
}
export const capOf = (t: Tank): Vec3 => ({ x: t.x, y: t.y1 - CAP.back, z: t.z1 + CAP.rise });

function tankSolids(t: Tank): PartSolid3d[] {
  const cap = capOf(t);
  return [prism('tank', tankRing(t), t.z0, t.z1), prism('cap', circle(cap, CAP.r), t.z1, cap.z)];
}

/** A dosing pump on a board's face: its body (half width, from the board's face
 *  at `back` out to `front`; the tapered skirt under it), its head (a disc on its
 *  front), the suction fitting under the head and the discharge valve on top of
 *  it. `x` is its middle. */
export interface Pump {
  x: number; hw: number; back: number; front: number; z0: number; z1: number;
  skirt: { hw: number; z0: number };
  head: { r: number; z: number; front: number };
  fitting: { hw: number; z0: number };
  valve: { hw: number; z1: number };
}
/** Where the head's fitting and valve stand: half way through the head's disc. */
export const fittingY = (p: Pump): number => (p.front + p.head.front) / 2;

function pumpSolids(p: Pump): PartSolid3d[] {
  const { x, head, fitting, valve, skirt } = p, fy = fittingY(p);
  return [
    prism('pump', rect(x - p.hw, p.back, x + p.hw, p.front), p.z0, p.z1),
    levelHull('pump', 'y', [{ x: x - skirt.hw, y: skirt.z0 }, { x: x + skirt.hw, y: skirt.z0 }, { x: x + p.hw, y: p.z0 }, { x: x - p.hw, y: p.z0 }], p.back, p.front),
    { kind: 'cyl', role: 'head', axis: 'y', at: [x, head.z], r: head.r, from: p.front, to: head.front },
    prism('head', rect(x - valve.hw, fy - valve.hw, x + valve.hw, fy + valve.hw), head.z + head.r, valve.z1),
    prism('head', rect(x - fitting.hw, fy - fitting.hw, x + fitting.hw, fy + fitting.hw), fitting.z0, head.z - head.r),
  ];
}
/** Where a pump's suction pipe leaves it: under its fitting. */
export const suctionOf = (p: Pump): Vec3 => ({ x: p.x, y: fittingY(p), z: p.fitting.z0 });
const PIPE_R = 3;

/** One product's figures: its footprint and height, and its solids. */
export interface DoserFigure {
  width: number;
  depth: number;
  height: number;
  solids: PartSolid3d[];
}

// ── The essence doser ─────────────────────────────────────────────────────────

/** Its shelf is one solid a face (`body.*`, the product's colour): seen from
 *  behind, its panel hides the tank and the pump; from a side, the near brace
 *  stands over the tank. */
export const DOSER = (() => {
  const width = 235, depth = 225, height = 630;
  const HW = width / 2, HD = depth / 2;
  /** The shelf's sheet: every part of it is 10 thick. */
  const SHEET = 10;
  /** The panel's front face; the base, the tank and the pump stand out from it. */
  const FRONT = -HD + SHEET;
  /** The stop across the middle of the base's front edge. */
  const STOP = { hw: 47.5, z1: 35 };
  /** A brace (one each side, its outer face flush with the shelf's) in (y, z): a
   *  45° band from the panel, 151.4 to 222.1 up it, down to the base. */
  const BRACE: Vec2[] = [{ x: FRONT, y: 151.4 }, { x: 38.9, y: SHEET }, { x: 109.6, y: SHEET }, { x: FRONT, y: 222.1 }];
  /** The tank, from the panel's face to the stop, on the base. */
  const tank: Tank = { x: 0, hw: 102.6, y0: FRONT, y1: HD - SHEET, z0: SHEET, z1: 297.1 };
  const pump: Pump = {
    x: 0, hw: 56.1, back: FRONT, front: FRONT + 120, z0: 441.8, z1: 578.2, skirt: { hw: 44.6, z0: 412.8 },
    head: { r: 46.6, z: 453.5, front: FRONT + 150 }, fitting: { hw: 11, z0: 370.6 }, valve: { hw: 11, z1: 551.2 },
  };
  const cap = capOf(tank), suction = suctionOf(pump);
  const solids: PartSolid3d[] = [
    prism('body.panel', rect(-HW, -HD, HW, FRONT), 0, height),
    prism('body.base', rect(-HW, FRONT, HW, HD), 0, SHEET),
    prism('body.stop', rect(-STOP.hw, HD - SHEET, STOP.hw, HD), SHEET, STOP.z1),
    levelHull('body.brace-r', 'x', BRACE, -HW, -HW + SHEET),
    levelHull('body.brace-l', 'x', BRACE, HW - SHEET, HW),
    ...tankSolids(tank),
    ...pumpSolids(pump),
    pipe([suction, { ...suction, z: cap.z }]),
  ];
  return { width, depth, height, HW, HD, SHEET, FRONT, STOP, BRACE, tank, pump, solids };
})();

// ── The Aromarium ─────────────────────────────────────────────────────────────

/** The drawing's figures: `d` across from its left end seen from the front, its
 *  heights from the floor, depths off the board's face. */
export const AR = (() => {
  const width = 853.1, depth = 220;
  const HW = width / 2, HD = depth / 2;
  const BOARD = { thick: 10, hw: 250, z0: 969.8, z1: 1469.8 };
  const FRONT = -HD + BOARD.thick;
  /** Seen from the front its left is local +x (it faces +y). */
  const x = (d: number): number => HW - d;
  const SHELF = { deep: 40, z0: 1189.8, z1: 1249.8 };
  /** The controller on the shelf, its window across its middle. */
  const BOX = { hw: 140, deep: 100, z0: SHELF.z1, z1: BOARD.z1, window: [1309.8, 1409.8] };
  const pumps: Pump[] = [235.55, 363.65, 491.65, 619.65].map((d) => ({
    x: x(d), hw: 47.85, back: FRONT, front: FRONT + 120, z0: 1042.6, z1: 1158.9, skirt: { hw: 38.1, z0: 1017.8 },
    head: { r: 39.7, z: 1052.6, front: FRONT + 150 }, fitting: { hw: 9.5, z0: 981.8 }, valve: { hw: 9.4, z1: 1135.9 },
  }));
  /** Side by side on the floor against the board's face, each under its pump
   *  (left to right as the pumps are). */
  const tanks: Tank[] = [102.6, 318.6, 534.5, 750.5].map((d) => ({ x: x(d), hw: 102.6, y0: FRONT, y1: FRONT + 210, z0: 0, z1: 284.4 }));
  /** The board, its shelf and controller, the pumps: from the wall out to the
   *  pumps' heads, as they stand on the drawing. */
  const unit = { hw: BOARD.hw, y0: -HD, y1: pumps[0].head.front };
  return { width, depth, HD, BOARD, FRONT, SHELF, BOX, pumps, tanks, unit, height: BOARD.z1 };
})();

export const movePump = (p: Pump, dy: number): Pump => ({ ...p, back: p.back + dy, front: p.front + dy, head: { ...p.head, front: p.head.front + dy } });
const moveTank = (t: Tank, dx: number, dy: number): Tank => ({ ...t, x: t.x + dx, y0: t.y0 + dy, y1: t.y1 + dy });

/** The board, the shelf, the controller and the pumps, `dy` along y. */
function aromariumUnitSolids(dy: number): PartSolid3d[] {
  const { BOARD, FRONT, SHELF, BOX, HD } = AR;
  const f = FRONT + dy;
  return [
    prism('body', rect(-BOARD.hw, -HD + dy, BOARD.hw, f), BOARD.z0, BOARD.z1),
    prism('shelf', rect(-BOARD.hw, f, BOARD.hw, f + SHELF.deep), SHELF.z0, SHELF.z1),
    prism('box', rect(-BOX.hw, f, BOX.hw, f + BOX.deep), BOX.z0, BOX.z1),
    prism('window', rect(-BOX.hw, f + BOX.deep, BOX.hw, f + BOX.deep + 2), BOX.window[0], BOX.window[1]),
    ...AR.pumps.map((p) => movePump(p, dy)).flatMap(pumpSolids),
  ];
}

/** A flexible pipe from a pump down to its tank's cap: dropping from the pump,
 *  sagging out from the board on its way across, and into the cap. */
function aromariumPipe(p: Pump, t: Tank): Vec3[] {
  const a = suctionOf(p), b = capOf(t);
  return [a, { ...a, z: a.z - 80 }, { x: (a.x + b.x) / 2, y: Math.max(a.y, b.y) + 30, z: (a.z + b.z) / 2 }, { ...b, z: b.z + 100 }, b];
}

/** The whole Aromarium as one part, its tanks in their places and piped to the
 *  pumps: what a copy placed before the tanks were parts of their own draws. */
const AROMARIUM: DoserFigure = {
  width: AR.width, depth: AR.depth, height: AR.height,
  solids: [
    ...aromariumUnitSolids(0),
    ...AR.tanks.flatMap(tankSolids),
    ...AR.pumps.map((p, i) => pipe(aromariumPipe(p, AR.tanks[i]))),
  ],
};

/** The Aromarium's wall unit: the board, its shelf and controller and the four
 *  pumps, from the wall out to the pumps' heads, in its own frame centred on its
 *  footprint (`dy` from the whole's). */
export const AROMARIUM_UNIT = (() => {
  const { unit, height } = AR;
  const dy = -(unit.y0 + unit.y1) / 2;
  const figure: DoserFigure = { width: 2 * unit.hw, depth: unit.y1 - unit.y0, height, solids: aromariumUnitSolids(dy) };
  return { figure, dy };
})();

/** The Aromarium's four aroma tanks as one: jerrycans side by side on the floor,
 *  as they stand under the pumps, their wall side −y, in their own frame centred
 *  on their footprint (`dy` from the whole's). */
export const AROMARIUM_TANKS = (() => {
  const { width, tanks } = AR;
  const y0 = tanks[0].y0, y1 = tanks[0].y1;
  const dy = -(y0 + y1) / 2;
  const moved = tanks.map((t) => moveTank(t, 0, dy));
  const figure: DoserFigure = { width, depth: y1 - y0, height: capOf(moved[0]).z, solids: moved.flatMap(tankSolids) };
  return { figure, dy, tanks: moved };
})();

/** A figure turned half round: its front to −y. */
const turnFigure = (f: DoserFigure): DoserFigure => ({ width: f.width, depth: f.depth, height: f.height, solids: f.solids.map(turnSolid) });

/** What a `doserModel` may name. */
export type DoserModel = 'aromarium' | 'aromarium-tanks' | 'aromarium-unit' | 'doser';

/** Each dosing product's figures as it is stored: turned, its front to −y. */
export const DOSER_FIGURES: Record<DoserModel, DoserFigure> = {
  aromarium: turnFigure(AROMARIUM),
  'aromarium-tanks': turnFigure(AROMARIUM_TANKS.figure),
  'aromarium-unit': turnFigure(AROMARIUM_UNIT.figure),
  doser: turnFigure(DOSER),
};

/** The dosing products there are figures for: what a `doserModel` may name. */
export const DOSER_MODELS = Object.keys(DOSER_FIGURES) as DoserModel[];

/** A dosing product's figures, by its product's `doserModel` (a field, not the
 *  id, which Duplicate replaces); none for a name it does not know. */
export function doserFigureOf(model: string | undefined): DoserFigure | null {
  return model && Object.prototype.hasOwnProperty.call(DOSER_FIGURES, model) ? DOSER_FIGURES[model as DoserModel] : null;
}
